import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import axiosInstance from "../../instance";

const url = process.env.REACT_APP_API_BASE_URL;

const initialState = {
  saving: false,
  savingRegistration: false,
  loadingTemplates: false,
  loadingQueue: false,
  loadingDeadLetters: false,
  loadingDashboard: false,
  loadingRegistrations: false,
  loadingShortcodes: false,
  processingQueue: false,
  templates: [],
  shortcodes: [],
  registrations: [],
  queueData: [],
  queueCount: 0,
  deadLetterData: [],
  deadLetterCount: 0,
  dashboardAnalytics: null,
  dashboardError: null,
  lastQueueResult: null,
};

export const fetchWhatsAppTemplates = createAsyncThunk(
  "whatsapp/fetchTemplates",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`${url}/api/v2/whatsapp/templates`);
      if (!response.data.success) {
        return rejectWithValue(response.data);
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data ?? { messages: { message: error.message } });
    }
  }
);

export const fetchWhatsAppShortcodes = createAsyncThunk(
  "whatsapp/fetchShortcodes",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`${url}/api/v2/shortcode/distinct-names`);
      if (!response.data.success) {
        return rejectWithValue(response.data);
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data ?? { messages: { message: error.message } });
    }
  }
);

export const fetchWhatsAppRegistrations = createAsyncThunk(
  "whatsapp/fetchRegistrations",
  async (params, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`${url}/api/v2/whatsapp/registrations`, { params });
      if (!response.data.success) {
        return rejectWithValue(response.data);
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data ?? { messages: { message: error.message } });
    }
  }
);

export const saveWhatsAppRegistration = createAsyncThunk(
  "whatsapp/saveRegistration",
  async (data, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`${url}/api/v2/whatsapp/registrations`, data);
      if (!response.data.success) {
        return rejectWithValue(response.data);
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data ?? { messages: { message: error.message } });
    }
  }
);

export const sendSingleWhatsApp = createAsyncThunk(
  "whatsapp/sendSingle",
  async (data, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`${url}/api/v2/whatsapp/single`, data);
      if (!response.data.success) {
        return rejectWithValue(response.data);
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data ?? { messages: { message: error.message } });
    }
  }
);

export const sendMultipleWhatsApp = createAsyncThunk(
  "whatsapp/sendMultiple",
  async (data, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`${url}/api/v2/whatsapp/multiple`, data);
      if (!response.data.success && !response.data?.data?.result) {
        return rejectWithValue(response.data);
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data ?? { messages: { message: error.message } });
    }
  }
);

export const fetchWhatsAppQueue = createAsyncThunk(
  "whatsapp/fetchQueue",
  async (params, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`${url}/api/v2/whatsapp/queue`, { params });
      if (!response.data.success) {
        return rejectWithValue(response.data);
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data ?? { messages: { message: error.message } });
    }
  }
);

export const fetchWhatsAppQueueMessage = createAsyncThunk(
  "whatsapp/fetchQueueMessage",
  async (id, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`${url}/api/v2/whatsapp/queue/${id}`);
      if (!response.data.success) {
        return rejectWithValue(response.data);
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data ?? { messages: { message: error.message } });
    }
  }
);

export const fetchWhatsAppDeadLetters = createAsyncThunk(
  "whatsapp/fetchDeadLetters",
  async (params, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`${url}/api/v2/whatsapp/dead-letter`, { params });
      if (!response.data.success) {
        return rejectWithValue(response.data);
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data ?? { messages: { message: error.message } });
    }
  }
);

export const processWhatsAppQueue = createAsyncThunk(
  "whatsapp/processQueue",
  async (limit = 25, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`${url}/api/v2/whatsapp/queue/process`, null, {
        params: { limit },
      });
      if (!response.data.success) {
        return rejectWithValue(response.data);
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data ?? { messages: { message: error.message } });
    }
  }
);

export const sendWhatsAppGroup = createAsyncThunk(
  "whatsapp/sendGroup",
  async ({ groupId, ...data }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`${url}/api/v2/whatsapp/group/${groupId}`, data);
      if (!response.data.success && !response.data?.data?.result) {
        return rejectWithValue(response.data);
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data ?? { messages: { message: error.message } });
    }
  }
);

export const sendWhatsAppManyGroups = createAsyncThunk(
  "whatsapp/sendManyGroups",
  async (data, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`${url}/api/v2/whatsapp/multi-group`, data);
      if (!response.data.success && !response.data?.data?.result) {
        return rejectWithValue(response.data);
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data ?? { messages: { message: error.message } });
    }
  }
);

export const uploadWhatsAppExcel = createAsyncThunk(
  "whatsapp/uploadExcel",
  async (formData, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.post(`${url}/api/v2/whatsapp/upload-excel`, formData, {
        headers: {
          "content-type": "multipart/form-data",
        },
      });
      if (!response.data.success && !response.data?.data?.result) {
        return rejectWithValue(response.data);
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data ?? { messages: { message: error.message } });
    }
  }
);

export const fetchWhatsAppDashboard = createAsyncThunk(
  "whatsapp/fetchDashboard",
  async (params, { rejectWithValue }) => {
    try {
      const response = await axiosInstance.get(`${url}/api/v2/whatsapp/dashboard`, { params });
      if (!response.data.success) {
        return rejectWithValue(response.data);
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data ?? { messages: { message: error.message } });
    }
  }
);

export const whatsappSlice = createSlice({
  name: "whatsapp",
  initialState,
  reducers: {
    clearWhatsAppQueueResult: (state) => {
      state.lastQueueResult = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchWhatsAppShortcodes.pending, (state) => {
        state.loadingShortcodes = true;
      })
      .addCase(fetchWhatsAppShortcodes.fulfilled, (state, action) => {
        state.loadingShortcodes = false;
        state.shortcodes = action.payload?.data?.result ?? [];
      })
      .addCase(fetchWhatsAppShortcodes.rejected, (state) => {
        state.loadingShortcodes = false;
        state.shortcodes = [];
      })
      .addCase(fetchWhatsAppRegistrations.pending, (state) => {
        state.loadingRegistrations = true;
      })
      .addCase(fetchWhatsAppRegistrations.fulfilled, (state, action) => {
        state.loadingRegistrations = false;
        state.registrations = action.payload?.data?.result ?? [];
      })
      .addCase(fetchWhatsAppRegistrations.rejected, (state) => {
        state.loadingRegistrations = false;
        state.registrations = [];
      })
      .addCase(saveWhatsAppRegistration.pending, (state) => {
        state.savingRegistration = true;
      })
      .addCase(saveWhatsAppRegistration.fulfilled, (state, action) => {
        state.savingRegistration = false;
        const saved = action.payload?.data?.result;
        if (!saved?.id) {
          return;
        }
        const next = state.registrations.filter((item) => item?.id !== saved.id);
        state.registrations = [saved, ...next];
      })
      .addCase(saveWhatsAppRegistration.rejected, (state) => {
        state.savingRegistration = false;
      })
      .addCase(fetchWhatsAppTemplates.pending, (state) => {
        state.loadingTemplates = true;
      })
      .addCase(fetchWhatsAppTemplates.fulfilled, (state, action) => {
        state.loadingTemplates = false;
        state.templates = action.payload?.data?.result ?? [];
      })
      .addCase(fetchWhatsAppTemplates.rejected, (state) => {
        state.loadingTemplates = false;
        state.templates = [];
      })
      .addCase(sendSingleWhatsApp.pending, (state) => {
        state.saving = true;
      })
      .addCase(sendSingleWhatsApp.fulfilled, (state, action) => {
        state.saving = false;
        state.lastQueueResult = action.payload?.data?.result ?? null;
      })
      .addCase(sendSingleWhatsApp.rejected, (state) => {
        state.saving = false;
      })
      .addCase(sendMultipleWhatsApp.pending, (state) => {
        state.saving = true;
      })
      .addCase(sendMultipleWhatsApp.fulfilled, (state, action) => {
        state.saving = false;
        state.lastQueueResult = action.payload?.data?.result ?? null;
      })
      .addCase(sendMultipleWhatsApp.rejected, (state) => {
        state.saving = false;
      })
      .addCase(sendWhatsAppGroup.pending, (state) => {
        state.saving = true;
      })
      .addCase(sendWhatsAppGroup.fulfilled, (state, action) => {
        state.saving = false;
        state.lastQueueResult = action.payload?.data?.result ?? null;
      })
      .addCase(sendWhatsAppGroup.rejected, (state) => {
        state.saving = false;
      })
      .addCase(sendWhatsAppManyGroups.pending, (state) => {
        state.saving = true;
      })
      .addCase(sendWhatsAppManyGroups.fulfilled, (state, action) => {
        state.saving = false;
        state.lastQueueResult = action.payload?.data?.result ?? null;
      })
      .addCase(sendWhatsAppManyGroups.rejected, (state) => {
        state.saving = false;
      })
      .addCase(uploadWhatsAppExcel.pending, (state) => {
        state.saving = true;
      })
      .addCase(uploadWhatsAppExcel.fulfilled, (state, action) => {
        state.saving = false;
        state.lastQueueResult = action.payload?.data?.result ?? null;
      })
      .addCase(uploadWhatsAppExcel.rejected, (state) => {
        state.saving = false;
      })
      .addCase(fetchWhatsAppQueue.pending, (state) => {
        state.loadingQueue = true;
      })
      .addCase(fetchWhatsAppQueue.fulfilled, (state, action) => {
        state.loadingQueue = false;
        state.queueData = action.payload?.data?.result ?? [];
        state.queueCount = action.payload?.total ?? 0;
      })
      .addCase(fetchWhatsAppQueue.rejected, (state) => {
        state.loadingQueue = false;
        state.queueData = [];
        state.queueCount = 0;
      })
      .addCase(fetchWhatsAppQueueMessage.fulfilled, (state, action) => {
        state.lastQueueResult = action.payload?.data?.result ?? state.lastQueueResult;
      })
      .addCase(fetchWhatsAppDeadLetters.pending, (state) => {
        state.loadingDeadLetters = true;
      })
      .addCase(fetchWhatsAppDeadLetters.fulfilled, (state, action) => {
        state.loadingDeadLetters = false;
        state.deadLetterData = action.payload?.data?.result ?? [];
        state.deadLetterCount = action.payload?.total ?? 0;
      })
      .addCase(fetchWhatsAppDeadLetters.rejected, (state) => {
        state.loadingDeadLetters = false;
        state.deadLetterData = [];
        state.deadLetterCount = 0;
      })
      .addCase(processWhatsAppQueue.pending, (state) => {
        state.processingQueue = true;
      })
      .addCase(processWhatsAppQueue.fulfilled, (state) => {
        state.processingQueue = false;
      })
      .addCase(processWhatsAppQueue.rejected, (state) => {
        state.processingQueue = false;
      })
      .addCase(fetchWhatsAppDashboard.pending, (state) => {
        state.loadingDashboard = true;
        state.dashboardError = null;
      })
      .addCase(fetchWhatsAppDashboard.fulfilled, (state, action) => {
        state.loadingDashboard = false;
        state.dashboardAnalytics = action.payload?.data?.result ?? null;
        state.dashboardError = null;
      })
      .addCase(fetchWhatsAppDashboard.rejected, (state, action) => {
        state.loadingDashboard = false;
        state.dashboardAnalytics = null;
        state.dashboardError = action.payload?.messages?.message ?? "Unable to load WhatsApp analytics";
      });
  },
});

export const { clearWhatsAppQueueResult } = whatsappSlice.actions;
export default whatsappSlice.reducer;
